import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "./ui/Icons";
// Foliage texture only — sits at 18% under the gradient, so it reads as a
// ground rather than a picture, and it stays distinct from the /climate-action
// hero which uses the soil shot.
import fieldImg from "../image/semi-di-pomodoro.jpg";

const STATS = ["accuracy", "diseases", "crops", "lossReduction"];

const STORY_URL =
  "https://africa.com/how-an-ai-app-is-helping-burundian-farmers-spot-crop-diseases-early/";

/**
 * The dark anchor of the homepage. Everything above and below sits on warm
 * paper, so this band carries the numbers and the case study.
 */
export default function BringingTech() {
  const { t } = useTranslation();

  return (
    <section className="relative isolate overflow-hidden bg-agri-forestDeep">
      <img
        src={fieldImg}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover opacity-[0.18]"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-agri-forestDeep via-agri-forestDeep/90 to-agri-forest" />

      <div className="relative container-x section">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-5">
            <p className="kicker-light">{t("design.tech.badge")}</p>
            <h2 className="mt-6 text-balance font-display text-h2 font-semibold leading-[1.08] tracking-[-0.026em] text-white sm:text-h2Lg">
              {t("design.tech.title")}
            </h2>
          </div>

          <div className="lg:col-span-6 lg:col-start-7 lg:pt-3">
            <p className="max-w-measure text-lede text-white/70">
              {t("design.tech.desc")}
            </p>
            <a
              href={STORY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 border-b border-agri-lime/40 pb-1 font-mono text-[0.6875rem] font-medium uppercase tracking-[0.12em] text-agri-lime transition-colors duration-200 hover:border-agri-lime"
            >
              {t("design.tech.readStory")}
              <ArrowUpRight className="h-3 w-3" />
            </a>
          </div>
        </div>

        {/* Figures set large in the display serif — the section's real content */}
        <dl className="mt-16 grid grid-cols-2 divide-white/15 border-t border-white/15 sm:mt-20 lg:grid-cols-4">
          {STATS.map((key, i) => (
            <div
              key={key}
              /* Divider rules are derived per index — stacking `first:` inside
                 a breakpoint variant makes the cascade order unreliable. */
              className={[
                "py-8 sm:py-10",
                i < 2 ? "border-b border-white/15 lg:border-b-0" : "",
                i % 2 === 1 ? "border-l border-white/15 pl-6" : "pr-6",
                i === 0 ? "lg:border-l-0 lg:pl-0" : "lg:border-l lg:border-white/15 lg:pl-8",
                "lg:pr-6",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <dd className="figure text-[2.75rem] leading-[0.95] text-agri-lime sm:text-[3.5rem]">
                {t(`design.tech.stats.${key}`)}
              </dd>
              <dt className="mt-4 max-w-[14ch] font-mono text-[0.6875rem] uppercase leading-relaxed tracking-[0.1em] text-white/65">
                {t(`design.tech.stats.${key}Label`)}
              </dt>
            </div>
          ))}
        </dl>

        <div className="mt-14">
          <Link to="/info" className="btn-outline-light">
            {t("design.tech.cta")}
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
